<template>
  <b-overlay :show="loading" rounded="sm" no-fade>
    <b-card v-if="event">
      <div class="d-flex align-items-start justify-content-between mb-1 flex-wrap">
        <div>
          <h4 class="mb-25">{{ event.title }}</h4>
          <b-badge :variant="govStatusVariant(event.status)" class="text-capitalize mr-50">{{ event.status }}</b-badge>
          <b-badge variant="light-info">{{ govTypeLabel(event.event_type) }}</b-badge>
        </div>
        <div class="d-flex flex-wrap">
          <b-button variant="outline-secondary" size="sm" class="mr-50 mb-50" :to="{ name: 'governance-events' }">
            <feather-icon icon="ArrowLeftIcon" class="mr-25" />Back
          </b-button>
          <b-button
            v-if="event.can_edit"
            variant="outline-warning"
            size="sm"
            class="mr-50 mb-50"
            :to="{ name: 'governance-event-edit', params: { id: event.id } }"
          >
            <feather-icon icon="EditIcon" class="mr-25" />Edit
          </b-button>
          <b-button
            v-if="event.can_edit && event.status === 'scheduled'"
            variant="outline-danger"
            size="sm"
            class="mr-50 mb-50"
            @click="$bvModal.show('gov-cancel-modal')"
          >
            <feather-icon icon="XCircleIcon" class="mr-25" />Cancel event
          </b-button>
          <b-button
            v-if="event.can_edit && event.status === 'scheduled' && isPast"
            variant="outline-success"
            size="sm"
            class="mb-50"
            @click="markCompleted"
          >
            <feather-icon icon="CheckCircleIcon" class="mr-25" />Mark completed
          </b-button>
        </div>
      </div>

      <b-row>
        <b-col cols="12" md="6">
          <p class="mb-50"><strong>When:</strong> {{ govFormatRange(event) }}</p>
          <p class="mb-50" v-if="event.working_group"><strong>Working group:</strong> {{ event.working_group.group_name }}</p>
          <p class="mb-50" v-if="event.creator"><strong>Organiser:</strong> {{ event.creator.name }} {{ event.creator.surname }}</p>
          <p class="mb-50" v-if="event.admin"><strong>Event administrator:</strong> {{ event.admin.name }} {{ event.admin.surname }}</p>
          <p class="mb-50 text-danger" v-if="event.cancelled_reason"><strong>Cancelled:</strong> {{ event.cancelled_reason }}</p>
        </b-col>
        <b-col cols="12" md="6">
          <div v-if="event.description" class="border rounded p-1" style="white-space: pre-wrap;">{{ event.description }}</div>
        </b-col>
      </b-row>

      <hr />
      <h5>Parts</h5>
      <governance-event-parts :event="event" />

      <b-tabs class="mt-2">
        <!-- Invitees -->
        <b-tab active>
          <template #title>
            Invitees <b-badge variant="light-primary" class="ml-25">{{ event.invitees.length }}</b-badge>
          </template>
          <div class="d-flex flex-wrap align-items-center mb-1">
            <b-badge variant="light-success" class="mr-50 mb-50">Accepted: {{ countResponse("accepted") }}</b-badge>
            <b-badge variant="light-warning" class="mr-50 mb-50">Tentative: {{ countResponse("tentative") }}</b-badge>
            <b-badge variant="light-danger" class="mr-50 mb-50">Declined: {{ countResponse("declined") }}</b-badge>
            <b-badge variant="light-secondary" class="mr-50 mb-50">No response: {{ countResponse("pending") }}</b-badge>
            <b-badge variant="light-info" class="mr-50 mb-50">Attended: {{ attendedCount }}</b-badge>
            <b-badge v-if="event.can_see_requirements" variant="light-primary" class="mr-50 mb-50">Special requirements: {{ requirementsCount }}</b-badge>
            <b-button
              v-if="event.can_manage && ['scheduled', 'completed'].includes(event.status)"
              size="sm"
              variant="outline-primary"
              class="ml-auto mb-50"
              :disabled="sendingAttendance"
              @click="sendAttendanceEmails"
            >
              <feather-icon icon="MailIcon" class="mr-25" />Send attendance confirmation
            </b-button>
          </div>
          <p v-if="event.can_manage" class="text-muted small mb-1">
            Invitees who have not declined are emailed automatically once the meeting's last part has started, to confirm whether they attended.
            <span v-if="event.attendance_emails_sent_at">Last sent {{ govFormatDateTime(event.attendance_emails_sent_at) }}.</span>
          </p>
          <b-table :items="event.invitees" :fields="inviteeFields" small striped hover responsive show-empty empty-text="No invitees">
            <template #cell(name)="{ item }">
              {{ item.user ? `${item.user.name} ${item.user.surname}` : item.user_id }}
              <small class="d-block text-muted" v-if="item.user">{{ item.user.email }}</small>
            </template>
            <template #cell(role)="{ item }">{{ item.user ? item.user.role : "-" }}</template>
            <template #cell(response)="{ item }">
              <b-badge :variant="govResponseVariant(item.response)" class="text-capitalize">{{ item.response }}</b-badge>
              <small class="d-block text-muted" v-if="item.responded_at">{{ govFormatDateTime(item.responded_at) }}</small>
            </template>
            <template #cell(special_needs)="{ item }">
              <span v-if="item.special_needs" style="white-space: pre-wrap;">{{ item.special_needs }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(attended)="{ item }">
              <b-form-select
                v-if="event.can_manage"
                :value="item.attended"
                :options="attendanceOptions"
                size="sm"
                style="min-width: 140px;"
                :disabled="savingAttendance"
                @change="(val) => setAttended(item, val)"
              />
              <span v-else>{{ attendanceLabel(item.attended) }}</span>
              <small class="d-block text-muted" v-if="item.attended !== null && item.attendance_source">
                {{ item.attendance_source === "self" ? "Confirmed by invitee" : "Recorded by organiser" }}
              </small>
              <small class="d-block text-muted" v-else-if="item.attendance_email_sent_at">
                Email sent {{ govFormatDateTime(item.attendance_email_sent_at) }}
              </small>
            </template>
          </b-table>
        </b-tab>

        <!-- Materials -->
        <b-tab>
          <template #title>
            Agenda &amp; Materials <b-badge variant="light-primary" class="ml-25">{{ event.materials.length }}</b-badge>
          </template>
          <div v-for="kind in ['agenda', 'minutes', 'material']" :key="kind" class="mb-1">
            <h6 class="text-capitalize">{{ kind === "material" ? "Other materials" : kind }}</h6>
            <b-list-group v-if="materialsOf(kind).length">
              <b-list-group-item
                v-for="m in materialsOf(kind)"
                :key="m.id"
                class="d-flex justify-content-between align-items-center py-50"
              >
                <span>
                  {{ m.title }}
                  <small class="text-muted" v-if="m.file_name">({{ m.file_name }})</small>
                  <small class="text-muted" v-else-if="m.external_url">({{ m.external_url }})</small>
                  <small class="d-block text-muted" v-if="m.uploader">
                    {{ m.uploader.name }} {{ m.uploader.surname }}, {{ govFormatDateTime(m.created_at) }}
                  </small>
                </span>
                <span class="text-nowrap">
                  <b-button size="sm" variant="outline-primary" class="mr-50" @click="govDownloadMaterial(m)">
                    <feather-icon :icon="m.external_url ? 'ExternalLinkIcon' : 'DownloadIcon'" size="14" />
                  </b-button>
                  <b-button v-if="event.can_edit" size="sm" variant="outline-danger" @click="deleteMaterial(m)">
                    <feather-icon icon="TrashIcon" size="14" />
                  </b-button>
                </span>
              </b-list-group-item>
            </b-list-group>
            <p v-else class="text-muted small mb-0">None yet.</p>
          </div>

          <div v-if="event.can_edit" class="border rounded p-1 mt-1">
            <h6>Add agenda, minutes or material</h6>
            <b-row class="align-items-end">
              <b-col cols="12" md="2">
                <b-form-group label="Kind" class="mb-50">
                  <v-select v-model="newMaterial.kind" :options="kindOptions" :reduce="(o) => o.value" :clearable="false" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Title" class="mb-50">
                  <b-form-input v-model="newMaterial.title" placeholder="e.g. Minutes 12 Jan" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="File" class="mb-50">
                  <b-form-file v-model="newMaterial.file" size="sm" placeholder="Choose file" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="or Link" class="mb-50">
                  <b-form-input v-model="newMaterial.external_url" placeholder="https://" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="1">
                <b-button variant="primary" size="sm" class="mb-50" :disabled="uploading" @click="uploadMaterial">
                  <feather-icon icon="UploadIcon" size="14" />
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-tab>

        <!-- Reminders -->
        <b-tab title="Reminders">
          <b-table :items="event.reminders" :fields="reminderFields" small striped responsive show-empty empty-text="No reminders">
            <template #cell(is_sent)="{ item }">
              <b-badge :variant="item.is_sent ? 'light-success' : 'light-secondary'">
                {{ item.is_sent ? `Sent ${govFormatDateTime(item.sent_at)}` : "Pending" }}
              </b-badge>
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Cancel modal -->
    <b-modal id="gov-cancel-modal" title="Cancel governance event" ok-title="Cancel event" ok-variant="danger" cancel-title="Keep" @ok="cancelEvent">
      <p>Invitees who have not declined will be emailed a cancellation notice.</p>
      <b-form-group label="Reason (optional)">
        <b-form-textarea v-model="cancelReason" rows="3" />
      </b-form-group>
    </b-modal>
  </b-overlay>
</template>

<script>
import store from "@/store";
import router from "@/router";
import moment from "moment";
import vSelect from "vue-select";
import { onUnmounted } from "@vue/composition-api";
import governanceMixin, { registerGovernanceStore, unregisterGovernanceStore } from "./governanceMixin";
import GovernanceEventParts from "./GovernanceEventParts.vue";

export default {
  name: "GovernanceEventView",
  components: { vSelect, GovernanceEventParts },
  mixins: [governanceMixin],
  setup() {
    registerGovernanceStore();
    onUnmounted(() => unregisterGovernanceStore());
  },
  data() {
    return {
      loading: false,
      event: null,
      savingAttendance: false,
      sendingAttendance: false,
      uploading: false,
      cancelReason: "",
      newMaterial: { kind: "material", title: "", file: null, external_url: "" },
      attendanceOptions: [
        { value: null, text: "Not recorded" },
        { value: true, text: "Attended" },
        { value: false, text: "Did not attend" },
      ],
      reminderFields: [
        { key: "days_before", label: "Days before" },
        { key: "message", label: "Message" },
        { key: "is_sent", label: "Status" },
      ],
    };
  },
  computed: {
    eventId() {
      return router.currentRoute.params.id;
    },
    isPast() {
      return this.event && this.event.start_at && moment(this.event.start_at).isBefore(moment());
    },
    meetingHref() {
      const url = this.event && this.event.meeting_url;
      return /^https?:\/\//i.test(url || "") ? url : `https://${url}`;
    },
    attendedCount() {
      return (this.event.invitees || []).filter((i) => i.attended === true).length;
    },
    requirementsCount() {
      return (this.event.invitees || []).filter((i) => i.special_needs).length;
    },
    /** Special requirements are only shown to the Event administrator and event editors. */
    inviteeFields() {
      const fields = [
        { key: "name", label: "Invitee", sortable: true },
        { key: "role", label: "Role" },
        { key: "response", label: "Response", sortable: true },
      ];
      if (this.event && this.event.can_see_requirements) fields.push({ key: "special_needs", label: "Special requirements" });
      fields.push({ key: "attended", label: "Attended", class: "text-center" });
      return fields;
    },
    kindOptions() {
      return ["agenda", "minutes", "material"].map((k) => ({ value: k, label: this.govCapitalize(k) }));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      store
        .dispatch("governance/getEvent", this.eventId)
        .then((res) => {
          this.event = res.data;
        })
        .catch((error) => {
          this.govToast("Could not load event", "danger", this.govErrorMessage(error));
          router.push({ name: "governance-events" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    countResponse(r) {
      return (this.event.invitees || []).filter((i) => i.response === r).length;
    },
    materialsOf(kind) {
      return (this.event.materials || []).filter((m) => m.kind === kind);
    },
    attendanceLabel(value) {
      if (value === null || value === undefined) return "Not recorded";
      return value ? "Attended" : "Did not attend";
    },
    setAttended(invitee, value) {
      this.savingAttendance = true;
      store
        .dispatch("governance/setAttendance", {
          id: this.event.id,
          attendance: [{ user_id: invitee.user_id, attended: value }],
        })
        .then((res) => {
          const row = (res.data || []).find((r) => r.user_id === invitee.user_id);
          if (row) Object.assign(invitee, { attended: row.attended, attendance_source: row.attendance_source });
        })
        .catch((error) => this.govToast("Could not save attendance", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.savingAttendance = false;
        });
    },
    sendAttendanceEmails() {
      this.$bvModal
        .msgBoxConfirm("Email invitees whose attendance is not recorded yet, asking them to confirm whether they attended?", {
          title: "Send attendance confirmation",
          okTitle: "Send",
          cancelTitle: "Cancel",
          centered: true,
        })
        .then((ok) => {
          if (!ok) return;
          this.sendingAttendance = true;
          store
            .dispatch("governance/sendAttendanceEmails", { id: this.event.id })
            .then((res) => {
              this.govToast(res.data.message, res.data.sent ? "success" : "warning");
              this.load();
            })
            .catch((error) => this.govToast("Could not send emails", "danger", this.govErrorMessage(error)))
            .finally(() => {
              this.sendingAttendance = false;
            });
        });
    },
    uploadMaterial() {
      if (!this.newMaterial.file && !this.newMaterial.external_url) {
        this.govToast("Choose a file or enter a link", "warning");
        return;
      }
      const fd = new FormData();
      fd.append("kind", this.newMaterial.kind);
      fd.append("title", this.newMaterial.title || (this.newMaterial.file ? this.newMaterial.file.name : this.newMaterial.external_url));
      if (this.newMaterial.file) fd.append("file", this.newMaterial.file);
      if (this.newMaterial.external_url) fd.append("external_url", this.newMaterial.external_url);

      this.uploading = true;
      store
        .dispatch("governance/uploadMaterial", { id: this.event.id, formData: fd })
        .then((res) => {
          this.event.materials.push(res.data);
          this.newMaterial = { kind: "material", title: "", file: null, external_url: "" };
          this.govToast("Material added");
        })
        .catch((error) => this.govToast("Upload failed", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.uploading = false;
        });
    },
    deleteMaterial(m) {
      this.$bvModal.msgBoxConfirm(`Remove "${m.title}"?`, { okVariant: "danger", okTitle: "Remove", centered: true }).then((ok) => {
        if (!ok) return;
        store
          .dispatch("governance/deleteMaterial", m.id)
          .then(() => {
            this.event.materials = this.event.materials.filter((x) => x.id !== m.id);
          })
          .catch((error) => this.govToast("Could not remove material", "danger", this.govErrorMessage(error)));
      });
    },
    cancelEvent() {
      this.patchStatus("cancelled", { cancelled_reason: this.cancelReason });
    },
    markCompleted() {
      this.patchStatus("completed");
    },
    patchStatus(status, extra = {}) {
      this.loading = true;
      const e = this.event;
      const data = {
        title: e.title,
        description: e.description,
        event_type: e.event_type,
        start_at: e.start_at,
        end_at: e.end_at,
        is_all_day: !!e.is_all_day,
        location: e.location,
        meeting_url: e.meeting_url,
        working_group_id: e.working_group_id,
        notify_invitees: e.notify_invitees !== false,
        status,
        ...extra,
      };
      store
        .dispatch("governance/updateEvent", { id: e.id, data })
        .then(() => {
          this.govToast(status === "cancelled" ? "Event cancelled" : "Event marked as completed");
          this.load();
        })
        .catch((error) => {
          this.loading = false;
          this.govToast("Could not update event", "danger", this.govErrorMessage(error));
        });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
